export const MASQUERADE_COMPANY_ID_KEY = 'masqueradeCompanyId'

/**
 * Read the company id the current user is masquerading as, if any.
 *
 * Superadmin/support/corporate users can masquerade as another company; the id is stored in
 * localStorage under `masqueradeCompanyId`. Returns `null` when not masquerading or when
 * localStorage is unavailable (e.g. SSR, privacy mode).
 */
export function getMasqueradeCompanyId(): string | null {
  try {
    const stored = window?.localStorage?.getItem(MASQUERADE_COMPANY_ID_KEY) || null
    // a literal 'null' string gets written by older code paths; treat it as "not masquerading" so
    // every consumer agrees rather than sending `company-id: null` to the API
    return stored === 'null' ? null : stored
  } catch {
    return null
  }
}

/**
 * Resolves which company the current tab should act as.
 *
 * An explicit `?masqueradeCompanyId=` wins over the stored value: localStorage is shared by every
 * tab on the origin, so letting a stale stored id beat the URL meant opening a link for company A
 * while another tab held company B silently kept you acting as B behind an A-looking URL (ENG-1061).
 *
 * Pure apart from persisting the URL param, so the precedence rule is directly testable — it was
 * previously a closure inside AuthorizeUser and therefore untested.
 */
export function resolveMasqueradeCompanyId({
  canMasquerade,
  search
}: {
  canMasquerade: boolean
  search?: string
}): string | null {
  if (!canMasquerade) {
    return null
  }

  const paramId = new URLSearchParams(search ?? '').get(MASQUERADE_COMPANY_ID_KEY)
  if (paramId && paramId !== 'null') {
    try {
      if (window?.localStorage?.getItem(MASQUERADE_COMPANY_ID_KEY) !== paramId) {
        window.localStorage.setItem(MASQUERADE_COMPANY_ID_KEY, paramId)
      }
    } catch {
      // localStorage unavailable (private mode) — the resolved id is still correct for this tab
    }
    return paramId
  }

  return getMasqueradeCompanyId()
}

/**
 * Build the `company-id` header used to forward masquerade context to the API.
 *
 * Returns `{ 'company-id': <id> }` when masquerading, otherwise an empty object so it can be
 * spread into request headers unconditionally. The API uses this header to scope a request to
 * the masqueraded company instead of the logged-in user's own company.
 */
export function getMasqueradeHeaders(): Record<string, string> {
  const masqueradeCompanyId = getMasqueradeCompanyId()
  return masqueradeCompanyId ? { 'company-id': masqueradeCompanyId } : {}
}

/**
 * Identity for anything that caches company-scoped data (the Apollo IndexedDB store, the
 * ApolloProvider remount key).
 *
 * `appDocs` takes no company argument and `$options`/`$rates` normalize to company-agnostic cache
 * ids, so the masqueraded company has to be baked into the cache identity itself — otherwise two
 * companies share the same cache slots and one franchise's settings can be saved over another's
 * (ENG-1061).
 *
 * Returns a bare `userId` when not masquerading, so existing offline caches keep their store name
 * and normal users aren't forced into a cold rehydrate.
 */
export function getCacheScopeKey(userId: string, masqueradeCompanyId?: string | null): string {
  const companyId = masqueradeCompanyId === undefined ? getMasqueradeCompanyId() : masqueradeCompanyId
  return companyId ? `${userId}:${companyId}` : userId
}
